import { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { BsCloudDownload } from "react-icons/bs";
import { CgSpinner } from "react-icons/cg";
import NodeInstance from "../../../../Global/nodeAxios";
import { setAppError, setBreadCrumb } from "../../../../store/userSlice";
import DownloadpdfPopup from "./downloadPDFPopup";
import Highlighter from "react-highlight-words";
import useServerPagination from "../../../../hooks/useServerPagination";
import PaginationControls from "../../../../components/SmartDataTable/PaginationControls";
import SearchFilter from "../../../../components/SmartDataTable/SearchFilter";

const NAV_BASE = "p-2 px-4 rounded-md text-white text-sm font-medium";
const NAV_ACTIVE = { backgroundColor: "#1f2937" };
const NAV_INACTIVE = { backgroundColor: "#ef4444" };
const navLink = (isActive) => ({ className: NAV_BASE, style: isActive ? NAV_ACTIVE : NAV_INACTIVE });



export default function Vrftable({ titles = [], setAddOrEdit }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);

  const [searchTerm, setSearchTerm] = useState("");
  const [downloadOption, setDownloadOption] = useState("all");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [popUp, setPopUp] = useState(false);
  const [popUpData, setPopUpData] = useState();

  const { data, paginationState, goToPage, changePageSize } = useServerPagination({
    apiEndpoint: "/dashboard/vrf-visa",
    token,
    defaultPageSize: 50,
    searchTerm,
    searchDebounceMs: 300,
  });

  useEffect(() => {
    dispatch(setBreadCrumb([{ label: "VISA REQUEST FORM SUMMARY", link: "" }]));
  }, [location.pathname, dispatch]);

  const addData = () => setAddOrEdit((p) => ({ ...p, open: true, method: "add" }));
  const Grdeskclick = () => setAddOrEdit((p) => ({ ...p, open: true, method: "add", routeName: "grDesk" }));

  const downloadFile = async () => {
    setDownloadLoading(true);
    try {
      const params = new URLSearchParams();
      if (downloadOption === "current") {
        params.append("page", paginationState.currentPage);
        params.append("limit", paginationState.pageSize);
      }
      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await NodeInstance.get(`/dashboard/vrf-visa/download${query}`, { responseType: "blob" });
      if (response.status === 200) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `VRF-SUMMARY`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        dispatch(setAppError({ msg: "Excel Downloaded", color: "success" }));
      }
    } catch (err) {
      dispatch(setAppError({ msg: err.message }));
    } finally {
      setDownloadLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "----";
    const date = new Date(dateString);
    const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
    return `${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()}`;
  };

  const searchWords = searchTerm ? searchTerm.split(/\s+/).filter(Boolean) : [];

  return (
    <div className="p-2 uppercase">

      {/* Toolbar */}
      <div className="flex-wrap gap-2 w-full items-center flex flex-row-reverse mb-4">
        <SearchFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search across all columns..."
          isLoading={paginationState.isLoading && searchTerm.length > 0}
        />
        <div className="flex gap-2 items-center">
          <select
            className={`select select-sm select-bordered focus:outline-none ${paginationState.isLoading || downloadLoading ? "cursor-not-allowed opacity-50 bg-gray-100" : "cursor-pointer"}`}
            value={downloadOption}
            onChange={(e) => setDownloadOption(e.target.value)}
            disabled={paginationState.isLoading || downloadLoading}
          >
            <option value="all">All Results ({paginationState.totalRecords})</option>
            <option value="current">Current Page ({data.length})</option>
          </select>
          <button
            className="p-2 px-4 rounded-md bg-blue disabled:bg-gray-500 text-white min-w-fit text-sm flex items-center gap-2"
            onClick={downloadFile}
            disabled={downloadLoading || paginationState.isLoading}
          >
            {downloadLoading && <CgSpinner className="animate-spin" />}
            <span>{downloadLoading ? "Downloading..." : "Download"}</span>
          </button>
        </div>
        <Link to="/dashboard/vrr-payment" {...navLink(location.pathname.startsWith("/dashboard/vrr-payment"))}>FINANCE DESK</Link>
        <Link to="/dashboard/visa-request-form/Gr-desk" {...navLink(location.pathname.includes("/Gr-desk"))} onClick={Grdeskclick}>GR DESK</Link>
        <Link to="/dashboard/visa-request-form/ops-1-summary" {...navLink(location.pathname.includes("/ops-1-summary"))} onClick={addData}>OPS 1 DESK</Link>
      </div>

      {/* Scrollable Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow max-h-[70vh]">
        <table className="min-w-full">
          <thead className="sticky top-0 z-20 bg-white border-b border-slate-200">
            <tr>
              {titles.map((title, i) => (
                <th key={i} className="px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white">
            {paginationState.isLoading ? (
              <tr>
                <td colSpan={titles.length} className="px-6 py-8 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <CgSpinner className="animate-spin text-xl text-blue-500" />
                    <span>Loading data...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={titles.length} className="px-6 py-8 text-center text-gray-400">
                  {searchTerm ? `No results found for "${searchTerm}"` : "No Data Found"}
                </td>
              </tr>
            ) : data.map((listItem, index) => (
              <tr key={listItem.id || index} className="hover:bg-gray-50">
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{(paginationState.currentPage - 1) * paginationState.pageSize + index + 1}</td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700"><Highlighter searchWords={searchWords} autoEscape={true} textToHighlight={listItem?.vrf_reference_number || ""} /></td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700"><Highlighter searchWords={searchWords} autoEscape={true} textToHighlight={formatDate(listItem?.created_at)} /></td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700"><Highlighter searchWords={searchWords} autoEscape={true} textToHighlight={String(listItem?.Nationality || "")} /></td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700"><Highlighter searchWords={searchWords} autoEscape={true} textToHighlight={listItem?.visa_stamp_city || ""} /></td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700"><Highlighter searchWords={searchWords} autoEscape={true} textToHighlight={listItem?.company_name || ""} /></td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700"><Highlighter searchWords={searchWords} autoEscape={true} textToHighlight={String(listItem?.total_visa || "")} /></td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700"><Highlighter searchWords={searchWords} autoEscape={true} textToHighlight={Number(listItem?.amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} /></td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700"><Highlighter searchWords={searchWords} autoEscape={true} textToHighlight={String(listItem.status === "OPS1 CONFIRMED" ? "PROCESS COMPLETED" : listItem.status === "REFUND INITIATED" ? "REFUND" : listItem.status || "")} /></td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700"><Highlighter searchWords={searchWords} autoEscape={true} textToHighlight={listItem?.updated_at ? formatDate(listItem.updated_at) : "----"} /></td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    className="rounded-md bg-white border border-gray-400 flex items-center justify-center"
                    style={{ width: "2rem", height: "2rem", cursor: "pointer" }}
                    onClick={() => { setPopUp(true); setPopUpData(listItem); }}
                  >
                    <BsCloudDownload size={"1rem"} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="mt-2">
        <PaginationControls
          currentPage={paginationState.currentPage}
          totalPages={paginationState.totalPages}
          pageSize={paginationState.pageSize}
          totalRecords={paginationState.totalRecords}
          onPageChange={goToPage}
          onPageSizeChange={changePageSize}
          isLoading={paginationState.isLoading}
        />
      </div>

      {popUp && <DownloadpdfPopup popUp={popUp} setPopUp={setPopUp} popUpData={popUpData} />}
    </div>
  );
}
